/**
 * 1on1 タイムラインの 1 週分カード
 *
 * 要約の各節・合意アクションの done トグル・原文（Drive）リンクを表示し、
 * 管理権限があれば要約の確認・privateNotes の編集と、1on1 の進め方の振り返りが見られる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business 1on1 セッション 1 件の表示（要約・合意アクション・成果物リンク・確認・privateNotes）
 * @graph-connects /one-on-one/pairs/sessions [calls] セッション更新 API
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import type { OneOnOneSessionResponse } from '@cortex/domain-product-innovation-lab-types';
import { useUpdateOneOnOneSession } from '../../../hooks/useOneOnOne.js';
import { OneOnOneLeaderFeedback } from './OneOnOneLeaderFeedback.js';

/** @graph-connects none */
interface OneOnOneSessionItemProps {
  pairId: string;
  session: OneOnOneSessionResponse;
  /** 要約の確認・privateNotes の編集ができるか */
  canManage: boolean;
}

/**
 * 要約ステータスのバッジ種別
 * @graph-connects none
 */
function statusVariant(
  status: OneOnOneSessionResponse['summaryStatus'],
): 'info' | 'warning' | 'success' {
  if (status === 'pending') return 'warning';
  if (status === 'generated') return 'info';
  return 'success';
}

/**
 * 要約の 1 節（空なら描画しない）
 * @graph-connects none
 */
function SummarySection({ title, items }: { title: string; items: readonly string[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-medium text-[var(--color-text-muted)]">{title}</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-[var(--color-text-primary)]">
        {items.map((item, index) => (
          <li key={`${String(index)}-${item.slice(0, 16)}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

/** @graph-connects none */
export function OneOnOneSessionItem({ pairId, session, canManage }: OneOnOneSessionItemProps) {
  const { t } = useTranslation();
  const update = useUpdateOneOnOneSession();
  const [notes, setNotes] = useState(session.privateNotes ?? '');
  const [notesDirty, setNotesDirty] = useState(false);
  const summary = session.summary;

  const toggleAction = (index: number, done: boolean) => {
    update.mutate({
      pairId,
      sessionId: session.sessionId,
      body: { agreedActionUpdates: [{ index, done }] },
    });
  };

  const confirm = () => {
    update.mutate({ pairId, sessionId: session.sessionId, body: { summaryStatus: 'confirmed' } });
  };

  const saveNotes = () => {
    update.mutate(
      { pairId, sessionId: session.sessionId, body: { privateNotes: notes } },
      {
        onSuccess: () => {
          setNotesDirty(false);
        },
      },
    );
  };

  return (
    <li
      className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4"
      data-testid={`one-on-one-session-${session.sessionId}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('oneOnOne.session.weekOf', { weekOf: session.weekOf })}
          </span>
          <Badge variant={statusVariant(session.summaryStatus)}>
            {t(`oneOnOne.status.${session.summaryStatus}`)}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {session.recording && (
            <a
              href={session.recording.webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-text-secondary)] underline"
            >
              {t('oneOnOne.session.openRecording')}
            </a>
          )}
          {session.transcript && (
            <a
              href={session.transcript.webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-text-secondary)] underline"
            >
              {t('oneOnOne.session.openTranscript')}
            </a>
          )}
        </div>
      </div>

      {session.attendees.length > 0 && (
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          {t('oneOnOne.session.attendees', { attendees: session.attendees.join(', ') })}
        </p>
      )}

      {!summary && (
        <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
          {session.summaryStatus === 'pending'
            ? t('oneOnOne.session.summaryPending')
            : t('oneOnOne.session.summaryEmpty')}
        </p>
      )}

      {summary && (
        <div className="mt-3 space-y-3">
          <SummarySection title={t('oneOnOne.summary.topics')} items={summary.topics} />
          <SummarySection
            title={t('oneOnOne.summary.feedbackGiven')}
            items={summary.feedbackGiven}
          />
          <SummarySection
            title={t('oneOnOne.summary.memberConcerns')}
            items={summary.memberConcerns}
          />
          {summary.agreedActions.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--color-text-muted)]">
                {t('oneOnOne.summary.agreedActions')}
              </p>
              <ul className="mt-1 space-y-1">
                {summary.agreedActions.map((action, index) => (
                  <li
                    key={`${String(index)}-${action.text.slice(0, 16)}`}
                    className="flex items-start gap-2 text-sm"
                  >
                    <Checkbox
                      checked={action.done}
                      disabled={update.isPending}
                      aria-label={action.text}
                      onChange={(event) => {
                        toggleAction(index, event.target.checked);
                      }}
                    />
                    <span
                      className={action.done ? 'line-through text-[var(--color-text-muted)]' : ''}
                    >
                      {action.text}
                      {action.dueWeek && (
                        <span className="ml-2 text-xs text-[var(--color-text-muted)]">
                          {t('oneOnOne.summary.dueWeek', { weekOf: action.dueWeek })}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <SummarySection title={t('oneOnOne.summary.nextCheck')} items={summary.nextCheck} />
        </div>
      )}

      {canManage && <OneOnOneLeaderFeedback pairId={pairId} session={session} />}

      {canManage && (
        <div className="mt-4 space-y-2 border-t border-[var(--color-border)] pt-3">
          <label className="block text-xs font-medium text-[var(--color-text-muted)]">
            {t('oneOnOne.session.privateNotes')}
            <Textarea
              className="mt-1"
              rows={3}
              value={notes}
              placeholder={t('oneOnOne.session.privateNotesPlaceholder')}
              onChange={(event) => {
                setNotes(event.target.value);
                setNotesDirty(true);
              }}
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={!notesDirty || update.isPending}
              onClick={saveNotes}
            >
              {t('oneOnOne.session.saveNotes')}
            </Button>
            {summary && session.summaryStatus === 'generated' && (
              <Button size="sm" variant="primary" disabled={update.isPending} onClick={confirm}>
                {t('oneOnOne.session.confirm')}
              </Button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
